/**
 * Seed démo commerciale — tenant demo-hospitol (Centre Médical Salama).
 * Complète le seed enrichi + flux pharmacie pour un dashboard crédible.
 */
import { loadStoredSession } from "@itaas/core/auth/auth.storage";
import {
  addPrescriptionLine,
  createConsultation,
  getPrescriptionByConsultation,
  listConsultations,
  listPharmacyQueuePrescriptions,
  markPrescriptionSent,
} from "@/core/clinical/clinical-store";
import type { PrescriptionLineInput } from "@/core/clinical/types";
import { IAM_TENANT_SLUG } from "@/core/config";
import { isDemoModeEnabled } from "@/core/demo/is-demo-mode-enabled";
import {
  createAdmission,
  listAdmissions,
  updateAdmissionStatus,
} from "@/core/admissions/admission-store";
import { createCoverageCase, countPendingCases, listCoverageCases } from "@/core/insurance/coverage-case-store";
import { listInsurers } from "@/core/insurance/insurer-store";
import { pharmacyInventoryApi } from "@/core/inventory/inventory-api";
import { listPatients, createPatient, updatePatient } from "@/core/patients/patient-store";
import type { Patient, PatientInput } from "@/core/patients/types";
import { patientFullName } from "@/core/patients/types";
import type { StockProduct, ValidatedInvoice } from "@/core/pharmacy/types";
import { listValidatedInvoices } from "@/core/pharmacy/pharmacy-store";
import { setProductPrice } from "@/core/pharmacy/pharmacy-store";
import { creditTreasuryAccount, listTreasuryMovements } from "@/core/treasury/treasury-store";
import type { BillingDocument } from "@/core/billing/billing-documents/types";
import {
  DEMO_COMMERCIAL_TAG,
  DEMO_COMMERCIAL_TARGETS,
  DEMO_COMMERCIAL_VERSION,
} from "./demo-commercial-targets";
import {
  patchAdmissionTimestamps,
  seedDemoPresentationReady,
} from "./demo-seed-utils";
import { getStorageItem, readJson, removeStorageItem, setStorageItem, writeJson } from "@/core/tenant/hospitol-storage";

const SEED_KEY = "hospitol.demoCommercialSeeded";
const INVOICES_KEY = "pharmacy.invoices";
const BILLING_KEY = "hospitol.billingDocuments";
const NOTIFICATIONS_KEY = "hospitol.notifications.inbox";

const FIRST_NAMES = [
  "Amina", "Junior", "Sarah", "David", "Grâce", "Patrick", "Marie", "Joseph", "Rachel", "Paul",
  "Esther", "Michel", "Chantal", "Emmanuel", "Naomi", "Fabrice", "Olivier", "Rebecca", "Samuel", "Divine",
] as const;

const LAST_NAMES = [
  "Kabongo", "Ilunga", "Tshilombo", "Mbuyi", "Mukendi", "Lukau", "Kasongo", "Mwamba", "Ngoy", "Kalala",
  "Mutombo", "Kapinga", "Luboya", "Mpunga", "Tshiala", "Kazadi", "Mwanza", "Banza", "Kalonji", "Mukuna",
] as const;

function comMotif(label: string): string {
  return `${DEMO_COMMERCIAL_TAG} ${label}`;
}

function pick<T>(arr: readonly T[], index: number): T {
  return arr[index % arr.length];
}

function writeInvoices(invoices: ValidatedInvoice[]): void {
  const kept = listValidatedInvoices().filter((inv) => !inv.id.startsWith("com-inv-"));
  writeJson(INVOICES_KEY, [...invoices, ...kept]);
}

function readBillingDocuments(): BillingDocument[] {
  return readJson<BillingDocument[]>(BILLING_KEY, []);
}

function writeBillingDocuments(docs: BillingDocument[]): void {
  writeJson(BILLING_KEY, docs);
  window.dispatchEvent(new CustomEvent("hospitol:billing-documents"));
}

function seedCommercialInsuredPatients(): void {
  const insurers = listInsurers();
  const mutuelle = insurers.find((i) => i.code === "MUT-SALAMA") ?? insurers[0];
  const asr = insurers.find((i) => i.code === "ASR-VIE") ?? insurers[1];
  if (!mutuelle && !asr) return;

  const uninsured = listPatients().filter((p) => !p.insurerId);
  const targets = [mutuelle, asr, mutuelle, asr, mutuelle].filter(Boolean);
  let linked = 0;

  for (let i = 0; i < uninsured.length && linked < 20; i++) {
    const patient = uninsured[i]!;
    const insurer = targets[linked % targets.length];
    if (!insurer) continue;
    updatePatient(patient.id, {
      firstName: patient.firstName,
      lastName: patient.lastName,
      gender: patient.gender,
      patientType: patient.patientType,
      phone: patient.phone,
      address: patient.address,
      birthDate: patient.birthDate,
      coverageType: "insurer",
      coverageName: insurer.name,
      insurerId: insurer.id,
      memberNumber: `MB-COM-${String(1000 + linked)}`,
      policyNumber: `POL-COM-2026-${String(linked + 1).padStart(2, "0")}`,
    });
    linked += 1;
  }
}

function seedCommercialTodayAdmissions(): void {
  const today = new Date();
  const todayIds = listAdmissions()
    .filter((a) => new Date(a.createdAt).toDateString() === today.toDateString())
    .map((a) => a.id);

  const target = DEMO_COMMERCIAL_TARGETS.patientsToday;
  if (todayIds.length >= target) {
    patchAdmissionTimestamps(todayIds.slice(0, target), today, 7);
    return;
  }

  const patients = listPatients();
  const motifs = [
    "Accueil — consultation générale",
    "Contrôle post-traitement",
    "Douleur abdominale",
    "Renouvellement ordonnance",
    "Certificat médical travail",
    "Fièvre persistante",
    "Suivi diabète",
    "Urgence — entorse cheville",
    "Vaccination enfant",
    "Bilan pré-opératoire",
    "Consultation cardiologie",
    "Douleur lombaire",
  ];

  const createdIds: string[] = [];
  for (let i = 0; i < motifs.length && todayIds.length + createdIds.length < target; i++) {
    const motif = comMotif(motifs[i]!);
    if (listAdmissions().some((a) => a.motif === motif)) continue;
    const patient = patients[(i + 8) % patients.length];
    if (!patient) continue;
    const adm = createAdmission({
      patientId: patient.id,
      channel: i % 7 === 0 ? "emergency" : "admission",
      motif,
      triageLevel: i % 7 === 0 ? "yellow" : null,
    });
    if (i % 4 === 0) updateAdmissionStatus(adm.id, "in_progress");
    else if (i % 5 === 0) updateAdmissionStatus(adm.id, "completed");
    createdIds.push(adm.id);
  }

  patchAdmissionTimestamps([...todayIds, ...createdIds], today, 7);
}

function paidAtInMonth(year: number, month: number, day: number, hour: number): Date {
  const maxDay = new Date(year, month + 1, 0).getDate();
  return new Date(year, month, Math.min(day, maxDay), hour, 30, 0, 0);
}

function buildPaidBillingDocument(input: {
  id: string;
  source: BillingDocument["source"];
  patient: Patient;
  product: StockProduct | undefined;
  amount: number;
  paidAt: Date;
  accountIndex: number;
  lineIndex: number;
}): BillingDocument {
  const { id, source, patient, product, amount, paidAt, accountIndex, lineIndex } = input;
  const accounts = [
    { id: "tr-ca-main", name: "Caisse principale", method: "cash" as const },
    { id: "tr-mm-mpesa", name: "Mobile Money M-Pesa", method: "mobile_money" as const },
    { id: "tr-mm-orange", name: "Mobile Money Orange", method: "mobile_money" as const },
    { id: "tr-bk-rawbank", name: "Compte RAWBANK", method: "bank_transfer" as const },
  ];
  const serviceBySource: Record<BillingDocument["source"], string> = {
    consultation: "Médecine générale",
    prescription: "Pharmacie",
    prescription_partial: "Pharmacie — ligne partielle",
    laboratory: "NFS + glycémie",
    imaging: "Radio thorax",
    emergency: "Box urgences",
    hospitalization: "Hospitalisation — Médecine",
    nursing: "Soins infirmiers",
    surgery: "Chirurgie",
    ambulance: "Transport SMUR",
    patient_consumables: "Trousse perfusion + gants",
  };
  const qty = source === "prescription" || source === "prescription_partial" ? 1 + (lineIndex % 2) : 1;
  const acct = accounts[accountIndex % accounts.length]!;

  return {
    id,
    source,
    kind: "invoice",
    status: "paid",
    number: `FAC-PAY-${id.slice(-6).toUpperCase()}`,
    patientId: patient.id,
    prescriptionId: null,
    prescriptionLineIds: [],
    labOrderId: null,
    imagingExamId: null,
    surgeryCaseId: null,
    consumableChargeId: null,
    consultationId: null,
    coverageCaseId: null,
    admissionId: null,
    stayId: null,
    serviceLabel: comMotif(serviceBySource[source]),
    payerType: patient.insurerId && lineIndex % 4 === 0 ? "insurer" : "patient",
    payerName: patientFullName(patient),
    insurerId: patient.insurerId ?? null,
    customer: {
      name: patientFullName(patient),
      phone: patient.phone,
      address: patient.address,
    },
    lines: [
      {
        id: `com-pl-${id}`,
        productId: source === "prescription" || source === "prescription_partial" ? (product?.id ?? `svc-${source}`) : `svc-${source}`,
        label:
          source === "prescription" || source === "prescription_partial"
            ? (product?.label ?? "Médicament démo")
            : serviceBySource[source],
        quantity: qty,
        unitPrice: Math.round(amount / qty),
      },
    ],
    subtotal: amount,
    createdAt: paidAt.toISOString(),
    updatedAt: paidAt.toISOString(),
    issuedAt: paidAt.toISOString(),
    proformaValidUntil: null,
    convertedFromId: null,
    hubInvoiceId: null,
    hubPublishedAt: null,
    payment: {
      method: acct.method,
      treasuryAccountId: acct.id,
      treasuryAccountName: acct.name,
      amount,
      reference: `PAY-COM-${id.slice(-6).toUpperCase()}`,
      attachment: null,
      paidAt: paidAt.toISOString(),
      paidBy: "demo-cashier",
    },
  };
}

function amountForBillingSource(source: BillingDocument["source"], index: number): number {
  if (source === "hospitalization") return 85_000 + (index % 3) * 12_000;
  if (source === "surgery") return 450_000 + (index % 2) * 50_000;
  if (source === "laboratory") return 25_000 + (index % 4) * 5000;
  if (source === "imaging") return 45_000 + (index % 3) * 8000;
  if (source === "prescription_partial") return 18_000 + (index % 3) * 2500;
  if (source === "patient_consumables") return 8_500 + (index % 4) * 1500;
  return 12_000 + (index % 9) * 3500;
}

function seedCommercialPaidInvoices(patients: Patient[], products: StockProduct[]): void {
  const all = readBillingDocuments();
  const existingPaid = all.filter((d) => d.id.startsWith("com-paid-"));
  if (existingPaid.length >= DEMO_COMMERCIAL_TARGETS.paidInvoices) return;

  const kept = all.filter((d) => !d.id.startsWith("com-paid-"));
  const docs: BillingDocument[] = [];
  const now = new Date();
  const monthSources: BillingDocument["source"][] = [
    "consultation",
    "prescription",
    "prescription_partial",
    "laboratory",
    "imaging",
    "emergency",
    "hospitalization",
    "nursing",
    "surgery",
    "ambulance",
    "patient_consumables",
  ];

  for (let i = 0; i < monthSources.length; i++) {
    const source = monthSources[i]!;
    const patient = patients[i % patients.length]!;
    const product = products[i % products.length];
    const paidAt = paidAtInMonth(now.getFullYear(), now.getMonth(), 2 + i * 2, 9 + (i % 5));
    docs.push(
      buildPaidBillingDocument({
        id: `com-paid-m-${String(i + 1).padStart(2, "0")}`,
        source,
        patient,
        product,
        amount: amountForBillingSource(source, i),
        paidAt,
        accountIndex: i,
        lineIndex: i,
      }),
    );
  }

  const sources: BillingDocument["source"][] = [
    "consultation",
    "prescription",
    "laboratory",
    "imaging",
    "emergency",
    "hospitalization",
    "nursing",
    "surgery",
    "ambulance",
    "patient_consumables",
  ];

  for (let i = 0; docs.length < DEMO_COMMERCIAL_TARGETS.paidInvoices; i++) {
    const patient = patients[(i + 3) % patients.length]!;
    const product = products[(i + 2) % products.length];
    const source = sources[i % sources.length]!;
    const paidAt = new Date(now);
    paidAt.setDate(paidAt.getDate() - (14 + (i % 45)));
    paidAt.setHours(10 + (i % 6), 15, 0, 0);

    docs.push(
      buildPaidBillingDocument({
        id: `com-paid-${String(i + 1).padStart(3, "0")}`,
        source,
        patient,
        product,
        amount: amountForBillingSource(source, i + 10),
        paidAt,
        accountIndex: i + 2,
        lineIndex: i + 10,
      }),
    );
  }

  writeBillingDocuments([...docs, ...kept]);
}

function seedCommercialPatients(): void {
  const current = listPatients().length;
  const toAdd = Math.max(0, DEMO_COMMERCIAL_TARGETS.patients - current);
  if (toAdd === 0) return;

  const seen = new Set(listPatients().map((p) => `${p.firstName} ${p.lastName}`));
  let added = 0;
  let i = 0;

  while (added < toAdd && i < toAdd * 3) {
    const firstName = pick(FIRST_NAMES, i + 40);
    const lastName = pick(LAST_NAMES, i + 17);
    const key = `${firstName} ${lastName}`;
    i += 1;
    if (seen.has(key)) continue;
    seen.add(key);

    const input: PatientInput = {
      firstName,
      lastName,
      gender: i % 2 === 0 ? "female" : "male",
      patientType: i % 6 === 0 ? "inpatient" : "outpatient",
      phone: `+243 82 ${String(500000 + i).slice(-6)}`,
      address: `${10 + (i % 40)} Av. Lumumba, Kinshasa`,
      birthDate: new Date(1970 + (i % 40), (i % 12) + 1, (i % 25) + 1).toISOString().slice(0, 10),
      notes: comMotif("Dossier patient"),
    };
    createPatient(input);
    added += 1;
  }
}

function seedCommercialInvoices(patients: Patient[], products: StockProduct[]): void {
  if (products.length === 0 || patients.length === 0) return;

  const invoices: ValidatedInvoice[] = [];
  const now = new Date();

  for (let i = 0; i < DEMO_COMMERCIAL_TARGETS.pharmacyInvoices; i++) {
    const product = products[i % products.length]!;
    const patient = patients[i % patients.length]!;
    const price = product.sellingPrice ?? 2500 + (i % 7) * 500;
    const qty = 1 + (i % 4);
    const daysAgo = i < 7 ? i : 7 + (i % 28);
    const validatedAt = new Date(now);
    validatedAt.setDate(validatedAt.getDate() - daysAgo);
    validatedAt.setHours(9 + (i % 8), 15, 0, 0);

    invoices.push({
      id: `com-inv-${String(i + 1).padStart(4, "0")}`,
      number: `FAC-COM-${String(1000 + i)}`,
      customer: {
        name: patientFullName(patient),
        phone: patient.phone,
        address: patient.address,
      },
      lines: [
        {
          id: `com-line-${i}`,
          productId: product.id,
          label: product.label,
          barcode: product.barcode,
          unitPrice: price,
          quantity: qty,
        },
      ],
      subtotal: price * qty,
      validatedAt: validatedAt.toISOString(),
      patientId: patient.id,
    });
  }

  writeInvoices(invoices);
}

function seedCommercialCashierQueue(patients: Patient[], products: StockProduct[]): void {
  const kept = readBillingDocuments().filter((d) => !d.id.startsWith("com-bill-"));
  const docs: BillingDocument[] = [];
  const now = new Date().toISOString();
  const product = products[0];

  for (let i = 0; i < DEMO_COMMERCIAL_TARGETS.cashierQueue; i++) {
    const patient = patients[i % patients.length]!;
    const amount = 3500 + i * 1250;
    docs.push({
      id: `com-bill-${String(i + 1).padStart(3, "0")}`,
      source: "prescription",
      kind: "invoice",
      status: "at_cashier",
      number: `FAC-CAISSE-${String(200 + i)}`,
      patientId: patient.id,
      prescriptionId: null,
      prescriptionLineIds: [],
      labOrderId: null,
      imagingExamId: null,
      surgeryCaseId: null,
      consumableChargeId: null,
      consultationId: null,
      coverageCaseId: null,
      admissionId: null,
      serviceLabel: comMotif("Dispensation pharmacie"),
      payerType: "patient",
      payerName: patientFullName(patient),
      insurerId: null,
      customer: {
        name: patientFullName(patient),
        phone: patient.phone,
        address: patient.address,
      },
      lines: [
        {
          id: `com-bdl-${i}`,
          productId: product?.id ?? `com-product-${i}`,
          label: product?.label ?? "Médicaments démo",
          quantity: 1,
          unitPrice: amount,
        },
      ],
      subtotal: amount,
      createdAt: now,
      updatedAt: now,
      issuedAt: now,
      proformaValidUntil: null,
      convertedFromId: null,
      hubInvoiceId: null,
      hubPublishedAt: null,
      payment: null,
    });
  }

  writeBillingDocuments([...docs, ...kept]);
}

function seedCommercialNotifications(): void {
  const samples = [
    { title: "Ordonnance en file pharmacie", body: "Nouvelle ordonnance à valider — file active.", eventType: "pharmacy.queue" },
    { title: "Stock bas — Amoxicilline", body: "Seuil minimum atteint sur MED-006.", eventType: "stock.low" },
    { title: "Paiement Mobile Money", body: "Encaissement M-Pesa 12 500 CDF enregistré.", eventType: "treasury.credit" },
    { title: "Accord assureur en attente", body: "Dossier Infliximab — validation partielle.", eventType: "insurance.pending" },
    { title: "Résultat labo disponible", body: "NFS — patient Kabongo Jean.", eventType: "lab.result" },
    { title: "Urgence orientée", body: "Triage jaune — salle 2.", eventType: "emergency.triage" },
    { title: "Facture en caisse", body: "6 dossiers patients en attente de paiement.", eventType: "billing.cashier" },
    { title: "Rappel rendez-vous", body: "Consultation cardiologie — 14h30.", eventType: "appointment.reminder" },
    { title: "Bon de sortie infirmerie", body: "Sortie maladie validée — dossier Amina.", eventType: "life-school.medical" },
    { title: "Compte rendu imagerie", body: "Radio thorax prête à consulter.", eventType: "imaging.report" },
    { title: "Inventaire pharmacie", body: "Valorisation stock mise à jour.", eventType: "stock.valuation" },
    { title: "Bienvenue — démo commerciale", body: "Centre Médical Salama — données commerciales chargées.", eventType: "demo.welcome" },
  ];

  const now = Date.now();
  const items = samples.slice(0, DEMO_COMMERCIAL_TARGETS.notifications).map((sample, i) => ({
    id: `com-ln-${String(i + 1).padStart(3, "0")}`,
    title: sample.title,
    body: sample.body,
    createdAt: new Date(now - i * 3_600_000).toISOString(),
    read: i >= 4,
    eventType: sample.eventType,
  }));

  const kept = (() => {
    try {
      const list = readJson<Array<{ id: string }>>(NOTIFICATIONS_KEY, []);
      return list.filter((n) => !n.id.startsWith("com-ln-"));
    } catch {
      return [];
    }
  })();

  setStorageItem(NOTIFICATIONS_KEY, JSON.stringify([...items, ...kept]));
  window.dispatchEvent(new CustomEvent("hospitol:notifications"));
}

function seedCommercialMobileMoney(): void {
  const today = new Date().toISOString().slice(0, 10);
  const existingToday = listTreasuryMovements({ limit: 200 }).filter(
    (m) =>
      m.paymentMethod === "mobile_money" &&
      m.reference?.startsWith("MM-COM-") &&
      m.createdAt.slice(0, 10) === today,
  );
  if (existingToday.length >= DEMO_COMMERCIAL_TARGETS.mobileMoneyPaymentsToday) return;

  const accounts = ["tr-mm-mpesa", "tr-mm-orange"];
  for (let i = existingToday.length; i < DEMO_COMMERCIAL_TARGETS.mobileMoneyPaymentsToday; i++) {
    creditTreasuryAccount({
      accountId: accounts[i % accounts.length]!,
      amount: 8500 + i * 3200,
      label: comMotif("Encaissement pharmacie"),
      paymentMethod: "mobile_money",
      reference: `MM-COM-${today}-${i + 1}`,
    });
  }
}

function lineInput(product: StockProduct, quantity: number): PrescriptionLineInput {
  return {
    productId: product.id,
    productLabel: product.label,
    quantity,
    frequency: "twice_daily",
    durationDays: 5,
    timing: ["morning", "evening"],
    instructions: "Démo commerciale",
  };
}

async function seedCommercialPrescriptions(products: StockProduct[]): Promise<void> {
  const queue = listPharmacyQueuePrescriptions();
  const needed = Math.max(0, DEMO_COMMERCIAL_TARGETS.prescriptionsQueue - queue.length);
  if (needed === 0 || products.length === 0) return;

  const patients = listPatients();
  const para = products.find((p) => p.label.toLowerCase().includes("paracétamol")) ?? products[0];
  if (!para) return;

  for (let i = 0; i < needed; i++) {
    const motif = comMotif(`Ordonnance file ${i + 1}`);
    if (listConsultations().some((c) => c.motif === motif)) continue;

    const patient = patients[(i + 5) % patients.length];
    if (!patient) continue;

    const consultation = createConsultation({
      patientId: patient.id,
      motif,
      diagnostic: "Traitement symptomatique — démo commerciale",
      acts: "Pharmacie hospitalière",
    });
    const prescription = getPrescriptionByConsultation(consultation.id);
    if (!prescription) continue;
    addPrescriptionLine(prescription.id, lineInput(para, 1 + (i % 3)));
    markPrescriptionSent(prescription.id);
  }
}

function seedCommercialInsurancePending(): void {
  const pending = countPendingCases();
  const needed = Math.max(0, DEMO_COMMERCIAL_TARGETS.insurancePending - pending);
  if (needed === 0) return;

  const patients = listPatients().filter((p) => p.insurerId);
  if (patients.length === 0) return;

  for (let i = 0; i < needed; i++) {
    const patient = patients[i % patients.length]!;
    const label = comMotif(`Accord préalable ${i + 1}`);
    const exists = listCoverageCases().some((c) => c.label === label);
    if (exists) continue;
    createCoverageCase({
      insurerId: patient.insurerId!,
      patientId: patient.id,
      kind: "prescription",
      label,
      amount: 45_000 + i * 12_000,
      memberNumber: patient.memberNumber ?? "MB-COM-001",
      policyNumber: patient.policyNumber ?? "POL-COM-2026",
    });
  }
}

function syncProductPrices(products: StockProduct[]): void {
  for (const product of products) {
    if (product.sellingPrice != null && product.sellingPrice > 0) {
      setProductPrice(product.id, product.sellingPrice);
    }
  }
}

function validateCommercialCounts(products: StockProduct[]): string {
  const patients = listPatients();
  const invoices = listValidatedInvoices().filter((i) => i.id.startsWith("com-inv-"));
  const cashier = readBillingDocuments().filter((d) => d.status === "at_cashier" && d.id.startsWith("com-bill-"));
  const paid = readBillingDocuments().filter((d) => d.status === "paid" && d.id.startsWith("com-paid-"));
  const todayAdm = listAdmissions().filter((a) => {
    const d = new Date(a.createdAt);
    const now = new Date();
    return d.toDateString() === now.toDateString();
  }).length;
  const queue = listPharmacyQueuePrescriptions().length;
  const today = new Date().toISOString().slice(0, 10);
  const mmToday = listTreasuryMovements({ limit: 100 }).filter(
    (m) => m.paymentMethod === "mobile_money" && m.createdAt.slice(0, 10) === today,
  ).length;

  let notifications = 0;
  try {
    notifications = readJson<unknown[]>(NOTIFICATIONS_KEY, []).length;
  } catch {
    notifications = 0;
  }

  return (
    `${patients.length} patients · ${todayAdm} admissions jour · ${products.length} produits ISC · ${invoices.length} factures pharmacie · ` +
    `${paid.length} factures payées · ${queue} ordonnances en file · ${cashier.length} en caisse · ${mmToday} Mobile Money jour · ` +
    `${countPendingCases()} assurances en attente · ${notifications} notifications`
  );
}

/** Alimente demo-hospitol avec volumes commerciaux (après seed enrichi + flux pharmacie). */
export async function seedDemoHospitolCommercial(): Promise<void> {
  if (!isDemoModeEnabled()) return;
  const tenantSlug = loadStoredSession()?.tenant?.slug ?? IAM_TENANT_SLUG;
  if (tenantSlug !== "demo-hospitol") return;
  if (getStorageItem(SEED_KEY) === DEMO_COMMERCIAL_VERSION) return;

  let products: StockProduct[] = [];
  try {
    products = (await pharmacyInventoryApi.listPharmacyProducts()).filter((p) => p.status === "ACTIVE");
  } catch {
    console.warn("[Hospitol démo] Seed commercial — catalogue ISC indisponible");
    return;
  }

  seedCommercialPatients();
  listInsurers();
  seedDemoPresentationReady();
  seedCommercialInsuredPatients();
  syncProductPrices(products);

  const patients = listPatients();
  seedCommercialTodayAdmissions();
  seedCommercialInvoices(patients, products);
  seedCommercialPaidInvoices(patients, products);
  seedCommercialCashierQueue(patients, products);
  seedCommercialNotifications();
  seedCommercialMobileMoney();
  await seedCommercialPrescriptions(products);
  seedCommercialInsurancePending();

  setStorageItem(SEED_KEY, DEMO_COMMERCIAL_VERSION);

  console.info(
    "[Hospitol démo] Seed commercial prêt —",
    validateCommercialCounts(products),
  );
}

export function resetDemoHospitolCommercial(): void {
  removeStorageItem(SEED_KEY);
}
